import React, { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { CheckCircle, Package, WhatsappLogo, Envelope, Phone } from '@phosphor-icons/react';

const fmtInr = (v) => `₹${Number(v || 0).toFixed(2)}`;

const STATUS_STYLES = {
  paid:       { bg: 'bg-green-100',  text: 'text-green-800',  label: 'Paid' },
  completed:  { bg: 'bg-green-100',  text: 'text-green-800',  label: 'Paid' },
  pending:    { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Pending' },
  failed:     { bg: 'bg-red-100',    text: 'text-red-800',    label: 'Failed' },
  refunded:   { bg: 'bg-blue-100',   text: 'text-blue-800',   label: 'Refunded' },
  abandoned:  { bg: 'bg-gray-100',   text: 'text-gray-700',   label: 'Abandoned' },
  cancelled:  { bg: 'bg-gray-100',   text: 'text-gray-700',   label: 'Cancelled' },
};

const PaymentPill = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.pending;
  return (
    <span data-testid={`payment-status-${status}`} className={`px-3 py-1 text-[10px] font-semibold uppercase tracking-wider ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  );
};

const buildWhatsAppMessage = (order, settings) => {
  const items = (order.items || []).map(i => `• ${i.product_name} × ${i.quantity} — ${fmtInr(i.price * i.quantity)}`).join('\n');
  const s = order.shipping_info || {};
  const num = order.order_number || order.id;
  const status = (STATUS_STYLES[order.payment_status] || {}).label || order.payment_status;
  const addr = `${s.address}, ${s.city}, ${s.state} - ${s.pincode}`;
  return `🌿 *${settings?.store_name || 'Veveru'} Order Confirmation*\n\n` +
    `*Order #:* ${num}\n` +
    `*Name:* ${s.name}\n` +
    `*Payment:* ${status}\n\n` +
    `*Items:*\n${items}\n\n` +
    `*Total:* ${fmtInr(order.total)}\n\n` +
    `*Delivery Address:*\n${addr}\n\n` +
    `Thank you for shopping with us!`;
};

const Orders = () => {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const justPlaced = searchParams.get('just_placed') === '1';
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/settings/checkout').then(r => setSettings(r.data)).catch(() => {});
    if (orderId) {
      fetchOrder(orderId);
    } else if (user) {
      fetchUserOrders();
    } else {
      setLoading(false);
    }
  }, [orderId, user]);

  const fetchUserOrders = async () => {
    try {
      const response = await api.get('/orders/user');
      setOrders(response.data);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const fetchOrder = async (id) => {
    try {
      const response = await api.get(`/orders/${id}`);
      setSelectedOrder(response.data);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="loading-spinner" data-testid="loading-spinner" />
      </div>
    );
  }

  if (orderId && selectedOrder) {
    const o = selectedOrder;
    const subtotal = o.subtotal != null ? o.subtotal : (o.items || []).reduce((s, i) => s + i.price * i.quantity, 0);
    const shipping = Number(o.shipping_charge || 0);
    const waMessage = encodeURIComponent(buildWhatsAppMessage(o, settings));
    const waLink = `https://wa.me/?text=${waMessage}`;
    const deliveryText = o.delivery_timeline || settings?.delivery_timeline;
    const supportEmail = settings?.support_email;
    const supportPhone = settings?.support_phone;
    const isPaid = ['paid', 'completed'].includes(o.payment_status);

    return (
      <div data-testid="order-detail-page" className="min-h-screen pt-24 pb-16 bg-[#F4F4F4]">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
            {isPaid ? (
              <CheckCircle size={64} weight="thin" className="text-green-600 mx-auto mb-4" />
            ) : (
              <Package size={64} weight="thin" className="text-[#641b4d] mx-auto mb-4" />
            )}
            <h1 data-testid="order-success-heading" className="text-4xl font-light tracking-tighter mb-2">
              {justPlaced && isPaid ? 'Order Placed Successfully!' : (isPaid ? 'Order Confirmed' : 'Order Details')}
            </h1>
            <p className="text-[#7a5e75] text-sm">Order Number</p>
            <p data-testid="order-number" className="text-2xl font-semibold text-[#641b4d] tracking-tight mb-3">
              {o.order_number || o.id}
            </p>
            <PaymentPill status={o.payment_status} />
          </motion.div>

          {/* Order Summary + Price Breakdown */}
          <div className="bg-white border border-[#E5E2DC] p-8 mb-6">
            <h2 className="text-2xl font-light mb-6">Order Summary</h2>
            <div className="space-y-4">
              {o.items.map((item, index) => (
                <div key={index} className="flex justify-between pb-4 border-b border-[#E5E2DC] last:border-0">
                  <div>
                    <p className="font-semibold" data-testid={`order-item-name-${index}`}>{item.product_name}</p>
                    <p className="text-sm text-[#7a5e75]">Quantity: {item.quantity}</p>
                    {item.customization?.finish && (
                      <p className="text-sm text-[#7a5e75]">Finish: {item.customization.finish}</p>
                    )}
                  </div>
                  <p className="font-semibold">{fmtInr(item.price * item.quantity)}</p>
                </div>
              ))}
              <div className="pt-2 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#7a5e75]">Product subtotal <span className="text-[10px] uppercase tracking-wider">(incl. GST)</span></span>
                  <span data-testid="order-subtotal">{fmtInr(subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#7a5e75]">Shipping</span>
                  <span data-testid="order-shipping">{shipping === 0 ? 'FREE' : fmtInr(shipping)}</span>
                </div>
              </div>
              <div className="pt-4 border-t border-[#E5E2DC] flex justify-between text-xl">
                <span className="font-semibold">{isPaid ? 'Total Paid' : 'Total Payable'}</span>
                <span data-testid="order-total" className="font-semibold">{fmtInr(o.total)}</span>
              </div>
              {o.razorpay_payment_id && (
                <p className="text-[10px] text-[#7a5e75] uppercase tracking-wider mt-3">
                  Razorpay Payment ID: <span data-testid="razorpay-payment-id">{o.razorpay_payment_id}</span>
                </p>
              )}
            </div>
          </div>

          {/* Shipping Address */}
          <div className="bg-white border border-[#E5E2DC] p-8 mb-6">
            <h2 className="text-2xl font-light mb-6">Shipping Address</h2>
            <div className="text-[#7a5e75] space-y-1 text-sm">
              <p className="text-[#3a2a35] font-semibold">{o.shipping_info.name}</p>
              <p>{o.shipping_info.address}</p>
              <p>{o.shipping_info.city}, {o.shipping_info.state} - {o.shipping_info.pincode}</p>
              <p>Phone: {o.shipping_info.phone}</p>
              <p>Email: {o.shipping_info.email}</p>
            </div>
          </div>

          {/* Delivery timeline */}
          {deliveryText && (
            <div data-testid="delivery-timeline" className="bg-[#FFF8E7] border-l-4 border-[#ffd034] p-5 mb-6 text-sm text-[#3a2a35]">
              <span className="uppercase tracking-widest text-[10px] text-[#7a5e75] block mb-1">Estimated Delivery</span>
              {deliveryText}
            </div>
          )}

          {/* Actions row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <a
              href={waLink}
              target="_blank"
              rel="noreferrer"
              data-testid="share-whatsapp-btn"
              className="btn-secondary w-full flex items-center justify-center gap-2 !bg-[#25D366] !text-white !border-[#25D366] hover:!bg-[#128C7E] hover:!border-[#128C7E]"
            >
              <WhatsappLogo size={20} weight="fill" />
              Share Order on WhatsApp
            </a>
            <Link to="/products" data-testid="continue-shopping-button" className="btn-primary w-full text-center">
              Continue Shopping
            </Link>
          </div>

          {/* Customer support */}
          <div data-testid="support-block" className="bg-white border border-[#E5E2DC] p-6">
            <h2 className="text-xs uppercase tracking-[0.2em] text-[#7a5e75] mb-3">Need Help?</h2>
            <div className="flex flex-col sm:flex-row gap-4 text-sm">
              {supportEmail && (
                <a href={`mailto:${supportEmail}`} data-testid="support-email" className="flex items-center gap-2 text-[#641b4d] hover:text-[#8c1666]">
                  <Envelope size={16} /> {supportEmail}
                </a>
              )}
              {supportPhone && (
                <a href={`tel:${supportPhone}`} data-testid="support-phone" className="flex items-center gap-2 text-[#641b4d] hover:text-[#8c1666]">
                  <Phone size={16} /> {supportPhone}
                </a>
              )}
              {!supportPhone && supportEmail && (
                <span className="text-xs text-[#7a5e75] italic">Phone support coming soon</span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="orders-page" className="min-h-screen pt-24 pb-16 bg-[#F4F4F4]">
      <div className="max-w-7xl mx-auto px-6">
        <h1 data-testid="orders-heading" className="text-4xl md:text-5xl font-light tracking-tighter mb-12">
          My Orders
        </h1>

        {orders.length === 0 ? (
          <div data-testid="no-orders-message" className="text-center py-24">
            <Package size={64} weight="thin" className="text-[#7a5e75] mx-auto mb-4" />
            <p className="text-[#7a5e75] mb-8">You haven't placed any orders yet</p>
            <Link to="/products" data-testid="start-shopping-button" className="btn-primary inline-block">
              Start Shopping
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {orders.map(order => (
              <motion.div
                key={order.id}
                data-testid={`order-${order.id}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white border border-[#E5E2DC] p-6"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="text-sm text-[#7a5e75]">Order # {order.order_number || order.id}</p>
                    <p className="text-sm text-[#7a5e75]">
                      Date: {new Date(order.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <PaymentPill status={order.payment_status} />
                </div>
                <div className="space-y-2 mb-4">
                  {order.items.map((item, index) => (
                    <div key={index} className="flex justify-between text-sm">
                      <span>{item.product_name} × {item.quantity}</span>
                      <span>{fmtInr(item.price * item.quantity)}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between items-center pt-4 border-t border-[#E5E2DC]">
                  <span className="font-semibold">Total: {fmtInr(order.total)}</span>
                  <Link
                    to={`/orders/${order.id}`}
                    data-testid={`view-order-${order.id}`}
                    className="text-sm text-[#bc64aa] hover:text-[#8c1666] uppercase tracking-wider"
                  >
                    View Details
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Orders;
